import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import LazyImage from '../lazy-image';
import PlayIcon from '../../images/svg/play-icon.svg';
import { getFluidGatsbyImage } from '../../utils/gatsby-storyblok-image-fixed';
import VideoPlayerOverlay from '../video-player-overlay.jsx';
import { motion } from 'framer-motion';
import mq from '../../utils/mq';

const VideoResourceItemContainer = styled(motion.div)`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const VideoResourceThumbnailContainer = styled.div`
  width: 100%;
  aspect-ratio: 16/9;
  position: relative;
  overflow: hidden;
  margin-bottom: 20px;
  cursor: pointer;
  border-radius: var(--Radius-Radius-3, 16px);
  background: var(--Color-Grey-1, #dee2e4);
  flex-shrink: 0;
`;

const PlayIconContainer = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  bottom: 0;
  right: 0;
  margin: 10px;
  color: #813dff;
  svg {
    width: 36px;
    height: 36px;
  }
`;

const VideoResourceTextContent = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  justify-content: space-between;
  align-items: flex-start;
  flex-grow: 1;
  ${mq.mobile(css`
    flex-grow: unset;
    gap: 0;
    height: unset;
  `)}
`;

const VideoResourceTitle = styled.h3`
  color: #161616;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
  text-wrap: pretty;
  margin-bottom: 12px;
  cursor: pointer;
`;

const VideoResourceMeta = styled.div`
  display: flex;
  gap: 0px 8px;
  flex-wrap: wrap;
`;

const VideoResourceCategory = styled.span`
  color: #161616;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
`;

const VideoResourceDuration = styled.span`
  color: #666;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
`;

interface VideoAsset {
  filename?: string;
  alt?: string;
}

interface VideoCategoryObj {
  name?: string;
}

interface VideoResourceItemData {
  title: string;
  length?: string | number;
  videoCategory?: VideoCategoryObj | string;
  videoThumbnail?: unknown;
  videoLink?: string;
  videoSource?: VideoAsset | null | undefined;
}

interface Props {
  item: VideoResourceItemData;
}

export default function VideoResourceItem({ item }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const videoUrl =
    item.videoLink && item.videoLink.trim()
      ? item.videoLink
      : item.videoSource?.filename || '';

  const hasVideo = !!videoUrl;
  const categoryName =
    typeof item.videoCategory === 'string'
      ? item.videoCategory
      : item.videoCategory?.name || '';
  const lengthNum = Number(item.length);
  const unit = lengthNum === 1 ? 'min' : 'mins';

  function openOverlay() {
    if (!hasVideo) return;
    setIsOpen(true);
  }

  // Narrow fluid image shape to match LazyImage expectations
  const rawFluid: any = getFluidGatsbyImage(item.videoThumbnail as any);
  const fluid =
    rawFluid &&
    typeof rawFluid.aspectRatio === 'number' &&
    typeof rawFluid.src === 'string' &&
    typeof rawFluid.srcSet === 'string' &&
    typeof rawFluid.sizes === 'string'
      ? rawFluid
      : undefined;

  return (
    <VideoResourceItemContainer
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      <VideoResourceThumbnailContainer onClick={openOverlay}>
        <motion.div
          style={{ height: '100%', width: '100%', willChange: 'transform' }}
          animate={
            isHovered
              ? {
                  scale: 1.03,
                  transition: { duration: 0.6, ease: [0.26, 1, 0.5, 1] }
                }
              : {
                  scale: 1,
                  transition: { duration: 0.3, ease: [0.26, 1, 0.5, 1] }
                }
          }
        >
          <LazyImage
            fluid={fluid}
            width="100%"
            height="100%"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </motion.div>
        <PlayIconContainer>
          <PlayIcon />
        </PlayIconContainer>
      </VideoResourceThumbnailContainer>
      <VideoResourceTextContent>
        <VideoResourceTitle onClick={openOverlay}>
          {item.title}
        </VideoResourceTitle>
        <VideoResourceMeta>
          <VideoResourceCategory>{categoryName}</VideoResourceCategory>
          {item.length !== undefined && item.length !== null && (
            <VideoResourceDuration>
              {lengthNum} {unit}
            </VideoResourceDuration>
          )}
        </VideoResourceMeta>
      </VideoResourceTextContent>
      {isOpen && hasVideo && (
        <VideoPlayerOverlay
          videoLink={videoUrl}
          handlePopupClose={() => setIsOpen(false)}
        />
      )}
    </VideoResourceItemContainer>
  );
}
